import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, PlayCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API, { formatApiError } from "@/lib/api";
import { isInAppBrowser } from "@/lib/inAppBrowser";
import { startPayment } from "@/lib/razorpay";

const EMPTY_FORM = { name: "", email: "", mobile: "" };

export default function CoursePurchaseModal({ course, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (course) {
      setForm(EMPTY_FORM);
      setErrors({});
      setSuccess(null);
    }
  }, [course]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!/^[0-9]{10}$/.test(form.mobile.trim())) errs.mobile = "Enter a valid 10-digit mobile number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleVerify = async (orderData, razorpayResponse) => {
    try {
      const { data } = await API.post("/courses/verify", {
        enrollment_id: orderData.enrollment_id,
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
      });
      setSuccess({ emailSent: data.email_sent, email: form.email.trim() });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setPaying(true);
    try {
      const { data: orderData } = await API.post("/courses/create-order", {
        course_id: course.id,
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
      });

      await startPayment({
        order: orderData,
        description: orderData.course_title,
        prefill: { name: form.name.trim(), email: form.email.trim(), contact: form.mobile.trim() },
        callbackPath: "/api/courses/razorpay-callback",
        cancelPath: "/payment-status?type=course&status=failed",
        onSuccess: (resp) => handleVerify(orderData, resp),
        onDismiss: () => setPaying(false),
        onFailure: (resp) => {
          API.post("/courses/failed", {
            enrollment_id: orderData.enrollment_id,
            reason: resp?.error?.description || "Payment failed",
          }).catch(() => {});
          setPaying(false);
          toast.error("Payment failed. Please try again.");
        },
      });
    } catch (e) {
      setPaying(false);
      const msg = formatApiError(e);
      if (msg === "payments_not_configured") {
        toast.error("Online payments are being set up. Please try again shortly.");
      } else {
        toast.error(msg);
      }
    }
  };

  const fields = [
    { key: "name", label: "Name", testid: "course-input-name", type: "text", placeholder: "Your full name" },
    { key: "email", label: "Email Id", testid: "course-input-email", type: "email", placeholder: "you@example.com" },
    { key: "mobile", label: "Mobile number", testid: "course-input-mobile", type: "tel", placeholder: "10-digit mobile number" },
  ];

  const lessons = course?.lessons || [];

  return (
    <Dialog open={!!course} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-[#FDFBF7] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="course-purchase-modal">
        {success ? (
          <div className="text-center py-6" data-testid="course-purchase-success">
            <CheckCircle2 className="mx-auto text-[#2D5A47]" size={52} />
            <h3 className="font-display text-2xl font-bold text-[#2D5A47] mt-4">
              Congratulations, {form.name.split(" ")[0]}!
            </h3>
            <p className="text-sm text-[#5A6E63] mt-2 leading-relaxed">
              Your course <strong>{course?.title}</strong> is now unlocked.
              {success.emailSent
                ? ` A confirmation has been emailed to ${success.email}.`
                : " Your confirmation will reach your email shortly."}
            </p>
            <p className="text-xs text-[#5A6E63] mt-3">
              Watch all lessons anytime in your dashboard. Sign in with this email &amp; mobile number.
            </p>
            <Link to="/dashboard" data-testid="course-go-to-dashboard-btn">
              <Button className="mt-6 rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] px-8">
                Watch My Course
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#2D5A47]">{course?.title}</DialogTitle>
              <DialogDescription className="text-[#5A6E63]">
                {course?.lesson_count || lessons.length} video lesson(s) &middot;{" "}
                <span className="font-semibold text-[#1E2A24]">&#8377;{course?.price}</span>
              </DialogDescription>
            </DialogHeader>

            {lessons.length > 0 && (
              <div className="rounded-2xl bg-[#FAF6F0] border border-[#2D5A47]/10 p-3 mt-1" data-testid="course-locked-lessons">
                <p className="text-xs font-semibold text-[#2D5A47] mb-2 flex items-center gap-1.5">
                  <Lock size={13} /> Unlocks after payment
                </p>
                <ul className="space-y-1.5">
                  {lessons.slice(0, 6).map((l, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-[#5A6E63]">
                      <PlayCircle size={14} className="text-[#D98E32] shrink-0" />
                      <span className="truncate">{l.title}</span>
                    </li>
                  ))}
                  {lessons.length > 6 && (
                    <li className="text-xs text-[#8a9a90]">+ {lessons.length - 6} more lesson(s)</li>
                  )}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="course-purchase-form">
              {fields.map(({ key, label, testid, type, placeholder }) => (
                <div key={key}>
                  <Label htmlFor={testid} className="text-xs text-[#5A6E63]">{label}</Label>
                  <Input
                    id={testid}
                    data-testid={testid}
                    type={type}
                    value={form[key]}
                    onChange={set(key)}
                    placeholder={placeholder}
                    className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20 focus-visible:ring-[#2D5A47]"
                  />
                  {errors[key] && (
                    <p data-testid={`course-error-${key}`} className="text-xs text-red-600 mt-1">{errors[key]}</p>
                  )}
                </div>
              ))}
              {isInAppBrowser() && (
                <div data-testid="inapp-payment-warning" className="bg-[#F9EBEA] border border-[#96690F]/30 rounded-xl p-3 text-xs text-[#96690F] leading-relaxed">
                  You will be taken to Razorpay&apos;s secure payment page. Pay with <strong>UPI, Card or Netbanking</strong> - you will be brought back here automatically after payment.
                </div>
              )}
              <Button
                type="submit"
                data-testid="course-complete-payment-button"
                disabled={paying}
                className="w-full rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] h-11 mt-2"
              >
                {paying ? (
                  <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Processing...</span>
                ) : (
                  <>Buy &amp; Unlock &middot; Pay &#8377;{course?.price}</>
                )}
              </Button>
              <p className="text-[11px] text-center text-[#5A6E63]">
                Secure payment via Razorpay. Lifetime access in your dashboard.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
